import {Dictionary, every, first, flatten, forEach, get, groupBy, isEmpty, isNull, keyBy, keys, map, nth, some,
  uniqBy} from 'lodash';
import {action} from 'mobx';

import {ReactNode} from 'react';

import Node, {NodePropertiesType} from './store/Node';
import Port from './store/Port';
import LinksGroup from './store/LinksGroup';
import Step from '../cablingMapEditor/store/Step';
import RackStore from './store/RackStore';
import {Change} from '../cablingMapEditor/store/ChangesStore';

import {generateId} from '../eptBuilder/utils';
import {SpeedType} from './types';

const speedExpr = new RegExp(/^(\d+(\.\d+)?)([gkmtb])$/, 'i');
const nameExpr = new RegExp(/[^a-z0-9]/, 'gi');

export const speedFromString = (speedString: string | null): SpeedType => {
  if (isNull(speedString)) return null;

  try {
    const match = speedString.match(speedExpr);
    if (match) {
      const [, value, , unit] = match;
      return {value: +value, unit};
    }
  } catch {
  }
  return null;
};

export const stringFromSpeed = (speed: SpeedType): string => {
  return `${speed?.value ?? ''}${speed?.unit ?? ''}`;
};

export const makeSafeName = (text: string): string => {
  return text.replace(nameExpr, '_');
};

export const randomPrefix = (): string => {
  return generateId().substr(-5);
};

// When the same set of items (nodes|linksGroups) get changed in batch, existing Change object must be reused.
// If the set is not the same - generate the new Change object.
export const pickBatchChangeFor = (items: (Node | LinksGroup)[], {changes}, registerChanges = true): Change => {
  // If given item(s) are nodes - their pairs will also be affected, thus
  // paired nodes must also be added to the set
  const itemsAffected = uniqBy(flatten(map(items, (item) => [...(item?.nodesInvolved ?? []), item])), 'id');
  const itemsById = keyBy(itemsAffected, 'id');

  let change = changes.current;
  if (change?.length === itemsAffected.length && every(change, ({id, isDelete}) => (!!itemsById[id] && !isDelete))) {
    // All node' properties changes must be tracked as a single change
    // Thus if the latest change equals to the current set of nodes - reuse it
    changes.splice();
  } else {
    // ... or create the new Change otherwise
    change = map(itemsAffected, (item) => Step.modification(item).inverted);
    if (registerChanges) changes.register(change);
  }
  return change;
};

// Register a single property change on a set of nodes
export const registerNodeChange = (change: Change, rackStore: RackStore, property?: NodePropertiesType,
  value?: any) => {
  const isBatch = change?.length > 1 && !(
    // Changing switch pair's properties should not be considered a batch change
    change?.length === 2 && rackStore.nodes?.[first(change)!.id!].isPairedWith(nth(change, 1)!.id!)
  );
  const isLabel = property?.endsWith('label');
  const stepsById = keyBy(change, 'id');

  if (isLabel) {
    // If label is being changed in batch previous label values must be removed for all
    // affected nodes in order not to interfere with taken labels set
    forEach(change, ({id}) => {
      rackStore.nodes[id!]?.setProperty('label', '');
    });
  }

  forEach(change, (step) => {
    const node = rackStore.nodes[step.id!];
    let newValue = value;
    if (isBatch && isLabel) {
      newValue = rackStore.pickUniqueLabel(value || node.role, node.isPaired, rackStore.usedLabels, true);
      // If node is paired and the pair is in selection and the pair has already been assigned
      // a label, just propagate this label
      if (node.isPaired && !!stepsById[node.pairedWith!.id]) {
        newValue = node.pairedWith!._label || newValue;
      }
    }

    // If no property is supplied - just dump the existing state
    if (property) {
      node.setProperty(
        property,
        newValue,
        true
      );
    }
    step.setResult(node);
  });
};

// Bulds all possible permutations for the given set of opitons
export const permute = (options, memo: any[] = [], result: any[] = []): any[] => {
  for (let index = 0, length = options.length; index < length; index++) {
    const current = options.splice(index, 1);
    if (options.length === 0) result.push(memo.concat(current));
    permute(options.slice(), memo.concat(current), result);
    options.splice(index, 0, current[0]);
  }
  return result;
};

const releasePorts = (ports: Port[]) => {
  forEach(ports, (port) => port.release());
};

export const tryPlacing = action((targetPorts: Port[], amongPorts: Port[]): Port[] | false => {
  if (isEmpty(targetPorts)) return amongPorts;

  const clones = map(amongPorts, (port) => port.clone());
  const portsBySpeed = groupBy(clones, 'speedString');

  // For each of speeds must be calculated independently
  const result = every(groupBy(targetPorts, 'speedString'), (ports, speedString) => {
    const availablePorts = portsBySpeed[speedString];
    const byRoles = groupBy(ports, 'role');
    // Trying all possible permutations of target roles
    return some(permute(keys(byRoles)), (rolesOrdered) => {
      // Reset taken ports states
      releasePorts(availablePorts);
      // For a single roles combination ...

      return every(rolesOrdered, (role) => {
        // ... make sure all target ports
        return every(byRoles[role], (targetPort) => {
          // ... find available local target
          return some(availablePorts, (myPort) => {
            if (myPort.correspondsTo(targetPort, true)) {
              // If one exists, mark it as taken and proceed
              myPort.isTaken = true;
              return true;
            }
          });
        });
      });
    });
  });

  return result ? clones : false;
});

export const makePortChannelError = (ids: number[], peeringType?: string): Dictionary<ReactNode> => {
  const count = ids.length;
  if (isEmpty(ids)) return {};
  const id = ids.join(', ');
  return peeringType ?
    {
      [peeringType]: (
        <>
          {'Port Channel ID '}
          <b>{id}</b>
          {` for ${peeringType} peer links is used more than once`}
        </>
      )
    } :
    {
      [id]: (
        <>
          {`Port Channel ID${count > 1 ? 's' : ''} `}
          <b>{id}</b>
          {` ${count > 1 ? 'are' : 'is'} used more than once`}
        </>
      )
    };
};

// Returns the value of the property if it is the same for all given nodes or the
// defaultValue otherwize
export const sameValue = (nodes: Node[], property: string, defaultValue?: any, comparator = property): any => {
  const firstNode = first(nodes);
  const firstValue = get(firstNode, comparator);
  return every(nodes, (node) => (get(node, comparator) === firstValue)) ? get(firstNode, property) : defaultValue;
};

// Pick a port channel ID range from the given range for the given node
export const pickPortChannelFromRange = (node: Node, rangeMin: number, rangeMax: number) => {
  let portChannelId = rangeMin;
  const neededCount = node.portChannelIdsNeeded;
  if (!neededCount || !rangeMin || !rangeMax || rangeMin > rangeMax) {
    // If no PCIDs needed or the range is invalid - just reset the node PCIDs
    node.fillWith({
      portChannelId: 0,
      portChannelExt: 0
    });
    return;
  }

  do {
    const minValue = portChannelId <= rangeMax ? portChannelId : 0;
    const maxValue = minValue + neededCount - 1 <= rangeMax ? minValue + neededCount - 1 : 0;
    node.setProperty('portChannelId', maxValue > 0 ? minValue : 0);
    node.setProperty('portChannelIdExt', maxValue);
    portChannelId += 1;
  } while (node.hasPortChannelErrors);
};
